/**
 * Financial Privacy — reusable UI: an eye toggle that reveals/hides financial
 * figures, plus the unlock dialog (PIN when set, else account password).
 *
 * Drop <FinancialPrivacyToggle /> anywhere inside <FinancialPrivacyProvider>.
 * Use <ProtectedMoney value="£123.45" /> (or the mask() helper) to render any
 * sensitive figure so it auto-masks when locked.
 */
import React, { useState } from "react";
import { Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useFinancialPrivacy, MASK } from "../lib/financialPrivacy";
import { formatApiError } from "../lib/api";

/** Renders a money value, masked when the privacy gate is locked. */
export const ProtectedMoney = ({ value, className = "", testid }) => {
  const { masked } = useFinancialPrivacy();
  return (
    <span className={className} data-testid={testid} data-masked={masked ? "true" : "false"}>
      {masked ? MASK : value}
    </span>
  );
};

const UnlockDialog = ({ open, onClose }) => {
  const { hasPin, unlock } = useFinancialPrivacy();
  const [credential, setCredential] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!credential) return;
    setBusy(true);
    setError(null);
    try {
      await unlock(credential);
      setCredential("");
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "object" && detail?.message ? detail.message : formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="financial-unlock-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-border rounded-xl p-5 w-full max-w-sm space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
            <Lock className="w-4 h-4 text-[#002FA7]" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-black leading-tight">Show financials</h3>
            <p className="label-mini text-muted-foreground">
              {hasPin ? "Enter your financial PIN" : "Enter your admin password"}
            </p>
          </div>
        </div>

        <Input
          type="password"
          inputMode={hasPin ? "numeric" : "text"}
          autoFocus
          value={credential}
          onChange={(e) => setCredential(e.target.value)}
          placeholder={hasPin ? "••••" : "Password"}
          className="font-mono-tab"
          data-testid="financial-unlock-input"
        />

        {error && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="financial-unlock-error">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose} data-testid="financial-unlock-cancel">
            Cancel
          </Button>
          <Button type="submit" className="flex-1" disabled={busy || !credential} data-testid="financial-unlock-submit">
            {busy ? "Checking…" : "Reveal"}
          </Button>
        </div>
      </form>
    </div>
  );
};

/** Eye toggle for the stats bar / any financial area. Hidden when mode is visible. */
export const FinancialPrivacyToggle = ({ className = "" }) => {
  const { mode, masked, revealed, revealLocal, hide } = useFinancialPrivacy();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (mode === "visible") return null;

  const onToggle = () => {
    if (!masked) { hide(); return; }
    if (mode === "protected") { setDialogOpen(true); return; }
    revealLocal(); // hidden mode → reveal without a credential
  };

  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-border hover:bg-secondary transition-colors ${className}`}
        data-testid="financial-privacy-toggle"
        aria-pressed={revealed}
        title={revealed ? "Hide financial figures" : "Show financial figures"}
      >
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{revealed ? "Hide" : "Reveal Financials"}</span>
        <span className="sm:hidden">{revealed ? "Hide" : "Reveal"}</span>
      </button>
      <UnlockDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

/** Small inline badge indicating the privacy state — optional, for headers. */
export const FinancialPrivacyBadge = () => {
  const { mode, masked } = useFinancialPrivacy();
  if (mode === "visible") return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground" data-testid="financial-privacy-badge">
      {masked ? <Lock className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3 text-[#16A34A]" />}
      {masked ? "Hidden" : "Visible"}
    </span>
  );
};
